function _EMOTION_STRINGIFIED_CSS_ERROR__() { return "You have tried to stringify object returned from `css` function. It isn't supposed to be used directly (e.g. as value of the `className` prop), but rather handed to emotion so it can handle it (e.g. as value of `css` prop)."; }
import { css } from '@emotion/react';
import { forwardRef, useMemo } from 'react';
import { su, theme, typography } from '@arc/frontend/ui-theme';
import { useForwardingRef } from '@arc/frontend/util-common';
export const ERROR_TRANSITION_LENGTH = '0.3s';
export const ERROR_TRANSITION_FUNC = 'ease-out';
const styles = {
  input: /*#__PURE__*/css(typography.inputText, {
    minWidth: 0,
    flexGrow: 1,
    padding: '0.45rem 0',
    resize: 'vertical',
    outline: 0,
    '&::placeholder': /*#__PURE__*/css(typography.placeholder, "opacity:1;" + (process.env.NODE_ENV === "production" ? "" : ";label:---placeholder;"))
  }, process.env.NODE_ENV === "production" ? "" : ";label:input;"),
  inputContainerNumeric: process.env.NODE_ENV === "production" ? {
    name: "1vgl3cm",
    styles: "input::-webkit-outer-spin-button, input::-webkit-inner-spin-button{-webkit-appearance:none;margin:0;}input[type='number']{-moz-appearance:textfield;}"
  } : {
    name: "gvxz5z-inputContainerNumeric",
    styles: "input::-webkit-outer-spin-button, input::-webkit-inner-spin-button{-webkit-appearance:none;margin:0;}input[type='number']{-moz-appearance:textfield;};label:inputContainerNumeric;",
    toString: _EMOTION_STRINGIFIED_CSS_ERROR__
  },
  inputContainer: /*#__PURE__*/css({
    display: 'flex',
    alignItems: 'center',
    width: '100%',
    paddingLeft: '0.75rem',
    paddingRight: '0.75rem',
    paddingTop: '0px',
    paddingBottom: '0px',
    cursor: 'text',
    backgroundColor: theme.colors.background.paper,
    borderRadius: 2,
    border: '1px solid',
    borderColor: theme.colors.input.border,
    transition: `border-color ${ERROR_TRANSITION_LENGTH} ${ERROR_TRANSITION_FUNC}`,
    '&:focus-within': /*#__PURE__*/css(su.shadow, {
      borderColor: theme.colors.input.focusedBorder
    }, process.env.NODE_ENV === "production" ? "" : ";label:--focus-within;")
  }, process.env.NODE_ENV === "production" ? "" : ";label:inputContainer;"),
  inputContainerError: /*#__PURE__*/css({
    borderColor: theme.colors.palettes.error.main,
    '&:focus-within': {
      borderColor: theme.colors.palettes.error.main
    }
  }, process.env.NODE_ENV === "production" ? "" : ";label:inputContainerError;"),
  inputContainerDisabled: /*#__PURE__*/css({
    cursor: 'default',
    color: theme.colors.input.disabledText,
    backgroundColor: theme.colors.input.disabledBackground,
    borderColor: theme.colors.input.disabledBorder
  }, process.env.NODE_ENV === "production" ? "" : ";label:inputContainerDisabled;"),
  inputContainerArchived: /*#__PURE__*/css({
    backgroundColor: theme.colors.background.paperTransparent
  }, process.env.NODE_ENV === "production" ? "" : ";label:inputContainerArchived;"),
  inputContainerReadOnly: /*#__PURE__*/css({
    backgroundColor: theme.colors.background.fullTransparent,
    cursor: 'default',
    borderColor: 'transparent',
    '&:focus-within': process.env.NODE_ENV === "production" ? {
      name: "ew917h",
      styles: "box-shadow:none;border-color:transparent"
    } : {
      name: "17vgevq---focus-within",
      styles: "box-shadow:none;border-color:transparent;label:--focus-within;",
      toString: _EMOTION_STRINGIFIED_CSS_ERROR__
    }
  }, process.env.NODE_ENV === "production" ? "" : ";label:inputContainerReadOnly;"),
  inputArchived: /*#__PURE__*/css({
    backgroundColor: theme.colors.background.fullTransparent
  }, process.env.NODE_ENV === "production" ? "" : ";label:inputArchived;"),
  inputReadOnly: /*#__PURE__*/css({
    backgroundColor: theme.colors.background.fullTransparent
  }, process.env.NODE_ENV === "production" ? "" : ";label:inputReadOnly;")
};
export const inputStyles = styles;
export const Input = forwardRef(
// eslint-disable-next-line @typescript-eslint/no-shadow
function Input(props, forwardedRef) {
  const {
    error,
    archived,
    disabled,
    readOnly,
    containerRef,
    inputRef: inputRefProp,
    startAdornment,
    endAdornment,
    containerStyle,
    inputStyle,
    containerClassName,
    forceAutoComplete,
    disablePasswordManager = true,
    autoCompleteOff,
    autoComplete,
    useRandomName = true,
    ...other
  } = props;
  const inputRef = useForwardingRef(null, [inputRefProp, forwardedRef]);
  const onContainerClick = event => {
    // Because the input container looks like part of the input it should
    // focus the input when the user clicks on the container.
    if (inputRef.current && event.currentTarget === event.target) {
      inputRef.current.focus();
    }
  };

  // We cannot use `useId` because it reuses the same values and Chrome will
  // remember those values and display autocomplete suggestions.
  //
  // `useMemo` is used to avoid extra DOM mutations.
  const randomName = useMemo(() => Math.random().toString(), []);
  return <div ref={containerRef} css={[styles.inputContainer, other?.type === 'number' && styles.inputContainerNumeric, error && styles.inputContainerError, disabled && styles.inputContainerDisabled, archived && styles.inputContainerArchived, readOnly && styles.inputContainerReadOnly, containerStyle, process.env.NODE_ENV === "production" ? "" : ";label:Input;"]} className={containerClassName} onClick={e => {
    onContainerClick(e);
  }}>
				{startAdornment}
				<input ref={inputRef} css={[styles.input, disabled && styles.inputContainerDisabled, archived && styles.inputArchived, readOnly && {
      display: 'none !important'
    }, inputStyle, process.env.NODE_ENV === "production" ? "" : ";label:Input;"]} disabled={disabled} readOnly={readOnly} {...other} name={useRandomName ? randomName : other.name} autoComplete={forceAutoComplete ? 'on' : autoCompleteOff ? 'off' : autoComplete || 'chrome-off'}
    // Disable Keeper password manager.
    className={disablePasswordManager ? 'keeper-ignore ' + other.className : other.className}
    // Disable LastPass password manager.
    data-lpignore={disablePasswordManager} aria-invalid={error} />

				{readOnly ? <div css={[styles.input, disabled && styles.inputContainerDisabled, archived && styles.inputArchived, styles.inputReadOnly, inputStyle, "resize:none;word-break:break-word;" + (process.env.NODE_ENV === "production" ? "" : ";label:Input;")]} {...other}>
						{other.value || <span>&nbsp;</span>}
					</div> : null}
				{endAdornment}
			</div>;
});